import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Edit as EditIcon,
  Fullscreen as MaximizeIcon,
  FullscreenExit as MinimizeIcon,
  ArrowForward as NextIcon
} from '@mui/icons-material';

interface PromptEditDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (prompt: string, model: string) => Promise<void>;
  initialPrompt: string;
  initialModel?: string;
  totalQueries?: number;
  title?: string;
  description?: string;
  confirmButtonText?: string;
  isProcessing?: boolean;
  batchMode?: string;
}

const PromptEditDialog: React.FC<PromptEditDialogProps> = ({
  open,
  onClose,
  onConfirm,
  initialPrompt,
  initialModel = 'openai-gpt-5-nano',
  totalQueries,
  title = 'Review & Edit Batch Processing Prompt',
  description = 'This is the final prompt that will be used for batch processing. You can review and edit it before confirming. All placeholder variables have been replaced with your configuration data.',
  confirmButtonText = 'Confirm & Start Batch Processing',
  isProcessing = false,
  batchMode
}) => {
  const theme = useTheme();
  const [editedPrompt, setEditedPrompt] = useState(initialPrompt);
  const [selectedModel, setSelectedModel] = useState(initialModel);
  const [isMaximized, setIsMaximized] = useState(false);
  
  // Check if Mini is available (only for batches under 100K queries)
  const isMiniAvailable = !totalQueries || totalQueries < 100000;
  
  // Update edited prompt and model when props change
  React.useEffect(() => {
    if (open) {
      setEditedPrompt(initialPrompt);
      // If Mini is not available and initial model is Mini, fall back to Nano
      if (!isMiniAvailable && initialModel === 'openai-gpt-5-mini') {
        setSelectedModel('openai-gpt-5-nano');
      } else {
        setSelectedModel(initialModel);
      }
      setIsMaximized(false);
    }
  }, [open, initialPrompt, initialModel, isMiniAvailable, batchMode]);
  
  const handleConfirm = async () => {
    await onConfirm(editedPrompt, selectedModel);
  };
  
  const handleClose = () => {
    if (!isProcessing) {
      onClose();
    }
  };
  
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth={isMaximized ? false : "md"}
      fullWidth={!isMaximized}
      fullScreen={isMaximized}
      PaperProps={{
        sx: isMaximized ? {} : {
          minHeight: '80vh',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EditIcon color="primary" />
              <Typography variant="h6">{title}</Typography>
            </Box>
            {totalQueries !== undefined && (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                Processing {totalQueries.toLocaleString()} queries
              </Typography>
            )}
          </Box>
          <Tooltip title={isMaximized ? "Minimize" : "Maximize"}>
            <IconButton 
              onClick={toggleMaximize}
              size="small"
              sx={{ ml: 'auto' }}
              disabled={isProcessing}
            >
              {isMaximized ? <MinimizeIcon /> : <MaximizeIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>
      <DialogContent 
        dividers
        sx={{ 
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          height: isMaximized ? 'calc(100vh - 180px)' : '600px'
        }}
      >
        <Alert severity="info" sx={{ flexShrink: 0 }}>
          {description}
        </Alert>
        
        {/* Only show AI model selection for OpenAI batch mode */}
        {batchMode !== 'guardrail' && (
          <>
            <FormControl fullWidth sx={{ flexShrink: 0 }}>
              <InputLabel id="model-select-label">AI Model</InputLabel>
              <Select
                labelId="model-select-label"
                id="model-select"
                value={selectedModel}
                label="AI Model"
                onChange={(e) => setSelectedModel(e.target.value)}
                disabled={isProcessing}
              >
                <MenuItem value="openai-gpt-5-nano">
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      GPT-5 Nano
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Fast and cost-effective • Best for simple classifications
                    </Typography>
                  </Box>
                </MenuItem>
                {isMiniAvailable && (
                  <MenuItem value="openai-gpt-5-mini">
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        GPT-5 Mini
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        More capable • Better for complex analysis and nuanced tasks
                      </Typography>
                    </Box>
                  </MenuItem>
                )}
              </Select>
            </FormControl>
            
            {/* Show info message if Mini is not available */}
            {!isMiniAvailable && totalQueries && (
              <Alert severity="info" sx={{ flexShrink: 0 }}>
                <Typography variant="body2">
                  <strong>GPT-5 Mini is only available for batches under 100,000 queries.</strong> GPT-5 Nano is recommended for large batches due to cost efficiency.
                </Typography>
              </Alert>
            )}
          </>
        )}
        
        <TextField
          fullWidth
          multiline
          value={editedPrompt}
          onChange={(e) => setEditedPrompt(e.target.value)}
          variant="outlined"
          disabled={isProcessing}
          sx={{
            flex: 1,
            '& .MuiInputBase-root': {
              height: '100%',
              alignItems: 'flex-start',
              fontFamily: 'monospace',
              fontSize: '0.875rem'
            },
            '& textarea': {
              height: '100% !important',
              overflow: 'auto !important'
            }
          }}
        />
        
        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
          Prompt length: {editedPrompt.length} characters
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button 
          onClick={handleClose}
          disabled={isProcessing}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleConfirm}
          disabled={isProcessing || !editedPrompt.trim()}
          endIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : <NextIcon />}
        >
          {isProcessing ? 'Processing...' : confirmButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PromptEditDialog;

